import { Spouse } from '@servicetitan/financing-api-client/src/prequals-api';

export interface ExtractSpouseInfoParams {
    /** Spouse info from form (can be from jobDetails.spouseInfo or direct spouseInfo) */
    spouseInfo?: {
        isMarried?: boolean;
        firstName?: string;
        lastName?: string;
        isAddressSameAsApplicant?: boolean;
        address?: any;
    };
    /** Fallback isMarried value (from simple marriage fields) */
    isMarried?: boolean;
    /** Address to use when isAddressSameAsApplicant is true */
    applicantAddress?: any;
}

/**
 * Extracts and formats spouse information for API submission
 *
 * Handles both simple marriage fields (isMarried only) and detailed fields
 * (full spouse name/address). Returns undefined if no spouse data exists.
 *
 * @param params - Spouse data and applicant address
 * @returns Formatted Spouse object or undefined
 */
export const extractSpouseInfo = (params: ExtractSpouseInfoParams): Spouse | undefined => {
    const { spouseInfo, isMarried: fallbackIsMarried, applicantAddress } = params;

    const isMarried = spouseInfo?.isMarried ?? fallbackIsMarried;

    if (isMarried === undefined) {
        return undefined;
    }

    if (isMarried === true && spouseInfo) {
        // Use applicant address only when explicitly set to true
        const useApplicantAddress = spouseInfo.isAddressSameAsApplicant === true;
        const spouseAddress = useApplicantAddress ? applicantAddress : spouseInfo.address;

        return {
            isMarried: true,
            firstName: spouseInfo.firstName,
            lastName: spouseInfo.lastName,
            address: spouseAddress,
        };
    }

    // API requires spouse object with isMarried: false even when not married
    if (isMarried === false) {
        return {
            isMarried: false,
        };
    }

    return undefined;
};
